import { Checkbox, Chip, Divider, Modal, Typography } from "@mui/material";
import React, {
  useCallback,
  useMemo,
  // useRef,
  useState,
} from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "utils/toast";
import * as Styled from "./styles";
import Button from "components/atoms/Button";
import TimePicker from "components/TimePcker";
import timerIcon from "assets/common/history_toggle_off.svg";
import {
  CoverageAreaProps,
  MassUpdateCoverageAreaItem,
} from "store/modules/Logistics/types.store";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "store/hooks";
import { setIsSpinnerVisible } from "store/modules/AppConfig/index.store";
import {
  handleGetWorkingAreasRequest,
  handleMassUpdateWorkingHoursRequest,
} from "store/modules/Logistics/thunk.store";

const WEEK_DAYS = {
  "0": "Monday",
  "1": "Tuesday",
  "2": "Wednesday",
  "3": "Thursday",
  "4": "Friday",
  "5": "Saturday",
  "6": "Sunday",
} as const;

interface MassEditWorkingHoursFormData {
  [key: `enabled-${string}`]: boolean;
  [key: `isOpen-${string}`]: boolean;
  [key: `open-${string}`]: string;
  [key: `close-${string}`]: string;
}

const MassEditWorkingHoursMenu = ({
  handleCloseMassEditWorkingHours,
  massEditData,
}: {
  handleCloseMassEditWorkingHours: () => void;
  massEditData: CoverageAreaProps[];
}) => {
  const initialValues = useMemo(
    () => ({
      ...Object.keys(WEEK_DAYS).reduce((acc, day) => {
        acc[`enabled-${day}`] = false;
        acc[`isOpen-${day}`] = false;
        acc[`open-${day}`] = "";
        acc[`close-${day}`] = "";
        return acc;
      }, {} as MassEditWorkingHoursFormData),
      "enabled-Holiday": false,
      "isOpen-Holiday": false,
      "open-Holiday": "",
      "close-Holiday": "",
    }),
    [],
  );

  const { control, watch, setValue, handleSubmit, reset } =
    useForm<MassEditWorkingHoursFormData>({
      defaultValues: initialValues,
    });

  const watchedValues = watch();
  const [isCloseConfirmationModalOpen, setIsCloseConfirmationModalOpen] =
    useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [showTimeError, setShowTimeError] = useState(false);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const hasChanges = useMemo(() => {
    return Object.keys(watchedValues).some((key) => {
      const currentValue =
        watchedValues[key as keyof MassEditWorkingHoursFormData];
      const initialValue = initialValues[key as keyof typeof initialValues];
      return currentValue !== initialValue;
    });
  }, [watchedValues, initialValues]);

  const validateTime = (time: string) => {
    if (!time) return false;
    const [hours, minutes] = time.split(":").map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return false;
    return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
  };

  const timeValidationRule = {
    validate: (value: string) => {
      if (!value) return true;
      return validateTime(value) || "Horário inválido";
    },
  };

  const handleClose = () => {
    if (hasChanges) {
      setIsCloseConfirmationModalOpen(true);
    } else {
      handleCloseMassEditWorkingHours();
    }
  };

  const handleConfirmClose = useCallback(() => {
    reset(initialValues);
    setIsCloseConfirmationModalOpen(false);
    handleCloseMassEditWorkingHours();
  }, [reset, initialValues, handleCloseMassEditWorkingHours]);

  const handleCancelClose = useCallback(() => {
    setIsCloseConfirmationModalOpen(false);
  }, []);

  const renderCloseConfirmationModal = useMemo(() => {
    return (
      <Modal open={isCloseConfirmationModalOpen} onClose={handleCancelClose}>
        <Styled.ConfirmationModalContainer>
          <Typography variant="h6">Atenção</Typography>
          <Typography variant="body2">
            Você possui alterações não salvas. Deseja realmente fechar sem
            salvar?
          </Typography>
          <Divider sx={{ borderColor: "black" }} />
          <Typography variant="body2" fontWeight="bold">
            Revendas selecionadas:
          </Typography>
          <Styled.ChipsContainer>
            {massEditData.map((coverageArea) => (
              <Chip
                variant="outlined"
                key={coverageArea.id}
                label={coverageArea.name}
              />
            ))}
          </Styled.ChipsContainer>
          <Styled.ConfirmationModalButtonsContainer>
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleCancelClose}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmClose}
            >
              Fechar sem salvar
            </Button>
          </Styled.ConfirmationModalButtonsContainer>
        </Styled.ConfirmationModalContainer>
      </Modal>
    );
  }, [
    isCloseConfirmationModalOpen,
    massEditData,
    handleCancelClose,
    handleConfirmClose,
  ]);

  const onSubmit = useCallback(
    async (data: MassEditWorkingHoursFormData) => {
      const openingHours = Object.keys(WEEK_DAYS)
        .filter((day) => data[`enabled-${day}`])
        .map((day) => {
          const isOpen = data[`isOpen-${day}`] || false;
          const openValue = data[`open-${day}`] || "";
          const closeValue = data[`close-${day}`] || "";
          return {
            day: WEEK_DAYS[day as keyof typeof WEEK_DAYS],
            open: isOpen ? openValue || "00:00" : "00:00",
            close: isOpen ? closeValue || "23:59" : "23:59",
            enabled: isOpen,
          };
        });

      const isHolidayOpen = data["isOpen-Holiday"] || false;
      const holidayOpenValue = data["open-Holiday"] || "";
      const holidayCloseValue = data["close-Holiday"] || "";
      const baseHolidayOpeningHours = data["enabled-Holiday"]
        ? [
            {
              day: "Holiday",
              open: isHolidayOpen ? holidayOpenValue || "00:00" : "00:00",
              close: isHolidayOpen ? holidayCloseValue || "23:59" : "23:59",
              enabled: isHolidayOpen,
            },
          ]
        : [];

      // Cria array de áreas de cobertura com os horários a serem ajustados
      const coverageAreas: MassUpdateCoverageAreaItem[] = massEditData.map(
        (coverageArea) => ({
          coverageAreaId: coverageArea.id,
          openingHours,
          ...(baseHolidayOpeningHours.length > 0 && {
            holidayOpeningHours: baseHolidayOpeningHours,
          }),
        }),
      );

      const payload = {
        authorization: "",
        dealerId: massEditData[0].dealer.id,
        coverageAreas,
      };

      dispatch(setIsSpinnerVisible(true));

      try {
        // Executa uma única chamada para atualizar todas as áreas
        await dispatch(handleMassUpdateWorkingHoursRequest(payload)).unwrap();

        await dispatch(
          handleGetWorkingAreasRequest({
            userId: massEditData[0].dealer.id,
          }),
        ).unwrap();

        toast("Horários atualizados com sucesso!", {
          type: "success",
          autoClose: 3000,
        });

        handleCloseMassEditWorkingHours();
      } catch (error: any) {
        toast(
          error?.response?.data?.message ||
            "Falha ao atualizar horários. Tente novamente.",
          {
            type: "error",
            autoClose: false,
          },
        );
      } finally {
        dispatch(setIsSpinnerVisible(false));
      }
    },
    [massEditData, dispatch, handleCloseMassEditWorkingHours],
  );

  const hasAnyDaySelected = useMemo(() => {
    const hasWeekDaySelected = Object.keys(WEEK_DAYS).some(
      (day) => watchedValues[`enabled-${day}`],
    );
    const hasHolidaySelected = watchedValues["enabled-Holiday"];
    return hasWeekDaySelected || hasHolidaySelected;
  }, [watchedValues]);

  const hasOpenDayWithoutTime = useMemo(() => {
    // Verifica dias da semana
    const weekDayWithoutTime = Object.keys(WEEK_DAYS).some((day) => {
      const isEnabled = watchedValues[`enabled-${day}`];
      const isOpen = watchedValues[`isOpen-${day}`];
      const openTime = watchedValues[`open-${day}`];
      const closeTime = watchedValues[`close-${day}`];
      return isEnabled && isOpen && (!openTime || !closeTime);
    });

    // Verifica feriado
    const isHolidayEnabled = watchedValues["enabled-Holiday"];
    const isHolidayOpen = watchedValues["isOpen-Holiday"];
    const holidayOpenTime = watchedValues["open-Holiday"];
    const holidayCloseTime = watchedValues["close-Holiday"];
    const holidayWithoutTime =
      isHolidayEnabled &&
      isHolidayOpen &&
      (!holidayOpenTime || !holidayCloseTime);

    return weekDayWithoutTime || holidayWithoutTime;
  }, [watchedValues]);

  const handleApplyClick = () => {
    if (!hasAnyDaySelected) {
      return;
    }
    if (hasOpenDayWithoutTime) {
      setShowTimeError(true);
      return;
    }
    setShowTimeError(false);
    handleSubmit(() => {
      setIsConfirmationModalOpen(true);
    })();
  };

  const handleConfirmSubmit = useCallback(() => {
    handleSubmit(onSubmit)();
    setIsConfirmationModalOpen(false);
  }, [handleSubmit, onSubmit]);

  const getSelectedDays = useMemo(() => {
    const selectedDays: Array<{
      day: string;
      dayName: string;
      isOpen: boolean;
      open: string;
      close: string;
    }> = [];

    Object.keys(WEEK_DAYS).forEach((day) => {
      if (watchedValues[`enabled-${day}`]) {
        const isOpen = watchedValues[`isOpen-${day}`] || false;
        selectedDays.push({
          day,
          dayName: WEEK_DAYS[day as keyof typeof WEEK_DAYS],
          isOpen,
          open: watchedValues[`open-${day}`] || "",
          close: watchedValues[`close-${day}`] || "",
        });
      }
    });

    if (watchedValues["enabled-Holiday"]) {
      const isHolidayOpen = watchedValues["isOpen-Holiday"] || false;
      selectedDays.push({
        day: "Holiday",
        dayName: "Feriado",
        isOpen: isHolidayOpen,
        open: watchedValues["open-Holiday"] || "",
        close: watchedValues["close-Holiday"] || "",
      });
    }

    return selectedDays;
  }, [watchedValues]);

  const renderConfirmationModal = useMemo(() => {
    return (
      <Modal
        open={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
      >
        <Styled.ConfirmationModalContainer>
          <Typography variant="h6">Atenção</Typography>
          <Typography variant="body2">
            O novo horário será aplicado em todas as áreas de cobertura
            selecionadas. Deseja salvar estas alterações?
          </Typography>
          <Divider sx={{ borderColor: "black" }} />
          <Typography variant="body2" fontWeight="bold">
            Revendas selecionadas:
          </Typography>
          <Styled.ChipsContainer>
            {massEditData.map((coverageArea) => (
              <Chip
                variant="outlined"
                key={coverageArea.id}
                label={coverageArea.name}
              />
            ))}
          </Styled.ChipsContainer>
          <Typography variant="body2" fontWeight="bold">
            Dias alterados:
          </Typography>
          <Styled.ConfirmationModalTableContainer>
            <Styled.ConfirmationModalTableHeader>
              <Styled.ConfirmationModalTableHeaderCell>
                Dia
              </Styled.ConfirmationModalTableHeaderCell>
              <Styled.ConfirmationModalTableHeaderCell>
                Abre
              </Styled.ConfirmationModalTableHeaderCell>
              <Styled.ConfirmationModalTableHeaderCell>
                Fecha
              </Styled.ConfirmationModalTableHeaderCell>
              <Styled.ConfirmationModalTableHeaderCell>
                Status
              </Styled.ConfirmationModalTableHeaderCell>
            </Styled.ConfirmationModalTableHeader>
            {getSelectedDays.map((dayData) => (
              <Styled.ConfirmationModalTableRow key={dayData.day}>
                <Styled.ConfirmationModalTableCell>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color="primary.main"
                  >
                    {dayData.day === "Holiday"
                      ? dayData.dayName
                      : t(
                          WEEK_DAYS[dayData.day as keyof typeof WEEK_DAYS]
                            .toString()
                            .toLowerCase() || "",
                        )}
                  </Typography>
                </Styled.ConfirmationModalTableCell>
                <Styled.ConfirmationModalTableCell>
                  <Typography variant="body2">
                    {dayData.isOpen ? dayData.open : "-"}
                  </Typography>
                </Styled.ConfirmationModalTableCell>
                <Styled.ConfirmationModalTableCell>
                  <Typography variant="body2">
                    {dayData.isOpen ? dayData.close : "-"}
                  </Typography>
                </Styled.ConfirmationModalTableCell>
                <Styled.ConfirmationModalTableCell>
                  <Typography variant="body2">
                    {dayData.isOpen ? "Aberto" : "Fechado"}
                  </Typography>
                </Styled.ConfirmationModalTableCell>
              </Styled.ConfirmationModalTableRow>
            ))}
          </Styled.ConfirmationModalTableContainer>
          <Styled.ConfirmationModalButtonsContainer>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setIsConfirmationModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmSubmit}
            >
              Confirmar
            </Button>
          </Styled.ConfirmationModalButtonsContainer>
        </Styled.ConfirmationModalContainer>
      </Modal>
    );
  }, [
    isConfirmationModalOpen,
    massEditData,
    getSelectedDays,
    handleConfirmSubmit,
    t,
  ]);

  return (
    <Styled.Container as="form" onSubmit={handleSubmit(onSubmit)}>
      <Typography variant="h5">Horário de funcionamento</Typography>
      <Typography variant="body2">
        Defina um <strong>horário único</strong> para as áreas selecionadas.
      </Typography>
      <Divider sx={{ borderColor: "black" }} />

      <Styled.TimePickerContainer>
        {Object.keys(WEEK_DAYS).map((day) => {
          const isDayEnabled = watchedValues[`enabled-${day}`];
          const isDayOpen = watchedValues[`isOpen-${day}`];
          return (
            <Styled.TimePickerRow key={day}>
              <Controller
                name={`enabled-${day}`}
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    checked={field.value || false}
                    size="small"
                  />
                )}
              />
              <Styled.TimePickerRowContent>
                <Typography
                  variant="body2"
                  width="200px"
                  color="primary.main"
                  fontWeight="bold"
                >
                  {t(
                    WEEK_DAYS[day as keyof typeof WEEK_DAYS]
                      .toString()
                      .toLowerCase() || "",
                  )}
                </Typography>
                <TimePicker
                  control={control}
                  name={`open-${day}`}
                  label="Abre"
                  setValue={setValue}
                  disabled={!isDayEnabled || !isDayOpen}
                  rules={{
                    minLength: 5,
                    ...timeValidationRule,
                    required:
                      isDayEnabled && isDayOpen
                        ? "Horário de abertura é obrigatório"
                        : false,
                  }}
                />
                <Typography variant="body2">até</Typography>
                <TimePicker
                  control={control}
                  name={`close-${day}`}
                  label="Fecha"
                  setValue={setValue}
                  disabled={!isDayEnabled || !isDayOpen}
                  rules={{
                    minLength: 5,
                    ...timeValidationRule,
                    required:
                      isDayEnabled && isDayOpen
                        ? "Horário de fechamento é obrigatório"
                        : false,
                  }}
                />
                <Styled.ContainerButton isActive={isDayOpen}>
                  <Controller
                    name={`isOpen-${day}`}
                    control={control}
                    render={({ field }) => (
                      <>
                        <Styled.CustomSwitch
                          {...field}
                          checked={field.value || false}
                          size="small"
                          color="success"
                          disabled={!isDayEnabled}
                        />
                        <Typography>
                          {field.value ? "Aberto" : "Fechado"}
                        </Typography>
                      </>
                    )}
                  />
                </Styled.ContainerButton>
              </Styled.TimePickerRowContent>
            </Styled.TimePickerRow>
          );
        })}
      </Styled.TimePickerContainer>

      <Styled.HolidayRowContainer>
        <Controller
          name="enabled-Holiday"
          control={control}
          render={({ field }) => (
            <Styled.HolidayRowPrefixContent>
              <Checkbox
                {...field}
                checked={field.value || false}
                size="small"
              />
              <Typography variant="body2">
                Estabelecer horário padrão para feriados
              </Typography>
            </Styled.HolidayRowPrefixContent>
          )}
        />
        <Styled.HolidayPickerRowContent
          isActive={!!watchedValues["isOpen-Holiday"]}
        >
          <Typography
            variant="body2"
            width="100px"
            color="primary.main"
            fontWeight="bold"
          >
            Feriado
          </Typography>
          {watchedValues["isOpen-Holiday"] ? (
            <>
              <TimePicker
                control={control}
                name="open-Holiday"
                label="Abre"
                setValue={setValue}
                disabled={
                  !watchedValues["enabled-Holiday"] ||
                  !watchedValues["isOpen-Holiday"]
                }
                rules={{
                  minLength: 5,
                  ...timeValidationRule,
                  required:
                    watchedValues["enabled-Holiday"] &&
                    watchedValues["isOpen-Holiday"]
                      ? "Horário de abertura é obrigatório"
                      : false,
                }}
              />
              <Typography variant="body2">até</Typography>
              <TimePicker
                control={control}
                name="close-Holiday"
                label="Fecha"
                setValue={setValue}
                disabled={
                  !watchedValues["enabled-Holiday"] ||
                  !watchedValues["isOpen-Holiday"]
                }
                rules={{
                  minLength: 5,
                  ...timeValidationRule,
                  required:
                    watchedValues["enabled-Holiday"] &&
                    watchedValues["isOpen-Holiday"]
                      ? "Horário de fechamento é obrigatório"
                      : false,
                }}
              />
            </>
          ) : (
            <Styled.TimerIconContainer>
              <Styled.TimerIcon src={timerIcon} alt="Horário não definido" />
            </Styled.TimerIconContainer>
          )}
          <Styled.ContainerButton isActive={!!watchedValues["isOpen-Holiday"]}>
            <Controller
              name="isOpen-Holiday"
              control={control}
              render={({ field }) => (
                <>
                  <Styled.CustomSwitch
                    {...field}
                    checked={field.value || false}
                    size="small"
                    color="success"
                    disabled={!watchedValues["enabled-Holiday"]}
                  />
                  <Typography>{field.value ? "Aberto" : "Fechado"}</Typography>
                </>
              )}
            />
          </Styled.ContainerButton>
        </Styled.HolidayPickerRowContent>
      </Styled.HolidayRowContainer>

      {showTimeError && hasOpenDayWithoutTime && (
        <Styled.ErrorMessage>
          <Typography variant="body2">
            É necessário preencher os horários de abertura e fechamento para os
            dias marcados como &quot;Aberto&quot;.
          </Typography>
        </Styled.ErrorMessage>
      )}

      <Styled.ButtonsContainer>
        <Button
          type="button"
          variant="outlined"
          color="inherit"
          onClick={handleClose}
        >
          Fechar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleApplyClick}
          disabled={!hasAnyDaySelected}
        >
          Aplicar
        </Button>
      </Styled.ButtonsContainer>
      {renderCloseConfirmationModal}
      {renderConfirmationModal}
    </Styled.Container>
  );
};

export default MassEditWorkingHoursMenu;
